import { Selection, EnterElement, scaleBand, ScaleTime } from "d3"
import { D3TracesWrapper } from "./D3TracesWrapper"
import { ModalityGraphGroupReactCallbacks, } from "../../../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityGraphConfig, ModalityGraphConfigWithDerivedTraces } from "../../../../Types/Graph"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { DeltaRegionJSON, HeatmapTraceConfigJSON, LineTraceConfigJSON } from "../../../../Types/Trace"
import { D3HeatmapGraphLegend } from "./D3HeatmapGraphLegend"
import { MULTI_GRAPH_SPACING, NUMERIC_VALUE_MARGIN } from "../../Constants"
import { D3SummaryValues } from "../../../D3/D3SummaryValues"

export class D3ModalityHeatmapGraph extends D3OneToOneRenderable<SVGGElement, SVGGElement, ModalityGraphConfig, ModalityGraphGroupReactCallbacks> {
	private yScale = scaleBand()
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private graph: ModalityGraphConfigWithDerivedTraces 
	private summaryValues?: D3SummaryValues
	private viewScale: ScaleTime<any, any, any>
	private foregroundClassName: string = "d3-heatmap-graph-foreground"
	private backgroundClassName: string = "d3-heatmap-graph-background"
	

	// Children
	private tracesWrapper?: D3TracesWrapper
	private legend?: D3HeatmapGraphLegend

	constructor(root: SVGGElement, config: ModalityGraphConfig, pageManager: TimeSeriesPageManager<ModalityPage>, reactCallbacks: ModalityGraphGroupReactCallbacks, viewScale: ScaleTime<any, any, any>) {
		super(root, config, "d3-modality-heatmap-graph", reactCallbacks)
		this.pageManager = pageManager
		this.viewScale = viewScale
		
		this.graph = {} as ModalityGraphConfigWithDerivedTraces

		this.updateDerivedState()
		this.render()
	}

	protected updateDerivedState(): void {
		this.graph = this.getDerivedGraph() as ModalityGraphConfigWithDerivedTraces

		this.yScale
			.domain([...new Set(this.graph.traces.map(trace => trace.id))])
			.range([0, this.config.height])

		// D3 does not allow us to use absolute padding. It is percentage based
		if (this.graph.traces.length > 3) {
			this.yScale.paddingInner(0.3)
		} else if (this.graph.traces.length > 1) {
			this.yScale.paddingInner(0.2)
		} else {
			this.yScale.paddingInner(0)
		}

		this.updateChildren()
	}

	public renderPage = (page: ModalityPage) => {
		this.tracesWrapper?.renderPage(page)
		this.summaryValues?.render()
	}

	public viewTimesChanged = () => {
		this.tracesWrapper?.render()
		this.summaryValues?.render()
	}

	public rescale = () => {
		this.tracesWrapper?.rescale()
	}

	public takeSnapshot = () => {
		this.tracesWrapper?.takeSnapshot()
	}

	public clearSnapshot = () => {
		this.tracesWrapper?.clearSnapshot()
	}

	public isRescaling = (): boolean => {
		return this.tracesWrapper?.isRescaling() ?? false
	}

	public getDerivedGraph = () => ({
		...this.config, 
		traces: this.config.traces
			.filter(trace => this.config.hideEmptyGraphs ? (trace.isVisible ?? true) : true) // Hide heatmaps that are not visible when the graph view is compressed.
			.map(traceConfig => ({ 
				...traceConfig, 
				graphId: this.config.id, 
				xScale: this.config.xScale, 
				yScale: this.yScale 
			}))
	})

    public autoScale = () => {} // required

	protected enter = (newGraph: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> => {
		const graphGroup = newGraph
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(0, ${this.config.offset})`)

		graphGroup
			.append("g")
			.attr("class", this.backgroundClassName)
			.each(this.createBackgroundChildren)

		graphGroup
			.append("g")
			.attr("class", this.foregroundClassName)
			.each(this.createForegroundChildren)

		return graphGroup
	}

	protected update = (updatedGraphs: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		updatedGraphs.attr("transform", `translate(0, ${this.config.offset})`)
		this.renderChildren()
		return updatedGraphs
	}

	protected createForegroundChildren = (config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.legend = new D3HeatmapGraphLegend(root, { traces: this.graph.traces as HeatmapTraceConfigJSON[], yScale: this.yScale, xOffset: this.config.width, graphId: this.config.id }, "d3-heatmap-graph-legend", this.reactCallbacks)
		
		const summaryValueConfigs = this.config.traces
			.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible)
			.map(config => ({...config, color: "black"}))

		const summaryValueColumns = this.config.summaryValueColumns ?? 1

		this.summaryValues = new D3SummaryValues(root, {
			traces: summaryValueConfigs as (LineTraceConfigJSON | DeltaRegionJSON)[],
			height: this.config.height + MULTI_GRAPH_SPACING, 
			x: this.config.width,
			y: -MULTI_GRAPH_SPACING/2,
			width: NUMERIC_VALUE_MARGIN, 
			rows: Math.ceil(summaryValueConfigs.length / summaryValueColumns), 
			columns: summaryValueColumns
		}, "d3-modality-heatmap-graph-summary-values", this.reactCallbacks, this.pageManager, this.viewScale)
	}

	protected createBackgroundChildren = (config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.tracesWrapper = new D3TracesWrapper(root, { traces: this.graph.traces }, this.pageManager, this.reactCallbacks)
	}


	protected updateChildren = () => {
		this.tracesWrapper?.updateConfig({ traces: this.graph.traces })
		this.legend?.updateConfig({ traces: this.graph.traces as HeatmapTraceConfigJSON[], yScale: this.yScale, xOffset: this.config.width, graphId: this.config.id })
		
		const summaryValueConfigs = this.config.traces
			.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible)
			.map(config => ({...config, color: "black"}))

		const summaryValueColumns = this.config.summaryValueColumns ?? 1

		this.summaryValues?.updateConfig({
			traces: summaryValueConfigs as (LineTraceConfigJSON | DeltaRegionJSON)[],
			height: this.config.height + MULTI_GRAPH_SPACING, 
			x: this.config.width,
			y: -MULTI_GRAPH_SPACING/2,
			width: NUMERIC_VALUE_MARGIN, 
			rows: Math.ceil(summaryValueConfigs.length / summaryValueColumns), 
			columns: summaryValueColumns
		})
	}

	private renderChildren = () => {
		this.tracesWrapper?.render()
		this.legend?.render()
	}
}
